// Main Method Writers submission form.
import React, { useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from "react";
import { ProofView, storedProofToken } from "./proof-view";
import { BioView } from "./bio-box";
import { ArtworkView } from "./artwork-view";
import { FinalView } from "./final-view";
import {
  Field,
  TextInput,
  EmailInput,
  GenreSelect,
  RichText,
  PlainTextarea,
  Checkbox,
  EditorsNote,
  checkNameInFirstSentence,
  sanitizeRichText,
} from "./fields";
import {
  useTweaks,
  TweaksPanel,
  TweakSection,
  TweakRadio,
  TweakToggle,
} from "./tweaks-panel";
import { PROOF_TURNSTILE_ACTION, type SubmissionPayload, type SubmissionResponse, type Genre, type Confirms, type HelpRequestType, type EmailStatus } from "./types";

const SECOND_PIECE_DEFAULT_CLOSED = true;

function SectionHeader({ roman, title, hint }: { roman: ReactNode; title: ReactNode; hint?: ReactNode }) {
  return (
    <header className="section-head">
      <div className="section-head-left">
        <div className="section-roman">{roman}.</div>
      </div>
      <div className="section-head-right">
        <h2 className="section-title">{title}</h2>
        {hint && <div className="section-hint">{hint}</div>}
      </div>
    </header>
  );
}

function Rule() {
  return (
    <div className="rule" aria-hidden="true">
      <span className="rule-line" />
      <span className="rule-orn">❦</span>
      <span className="rule-line" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Local-draft autosave. Form state is serialized to localStorage on every
// change (debounced), so a writer who closes the tab or steps away can pick
// up where they left off. Single-version key for now; bump the suffix when
// the draft shape changes incompatibly.
const DRAFT_KEY = "method-writers-draft-v1";
const DRAFT_DEBOUNCE_MS = 500;

interface DraftV1 {
  v: 1;
  savedAt: number;            // ms since epoch
  mode: string;
  unlockedSection: number;
  first: string;
  last: string;
  displayName: string;
  email: string;
  genre: string;
  title1: string;
  body1: string;
  title2: string;
  body2: string;
  secondOpen: boolean;
  bio: string;
  confirms: {
    c1: boolean; c2: boolean; c3: boolean;
    p1: boolean; p2: boolean;
    pr1: boolean; pr2: boolean; pr3: boolean;
  };
  artworkInterest: boolean;
}

function formatRelativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  try {
    return new Date(ms).toLocaleDateString();
  } catch {
    return `${days} days ago`;
  }
}

function App() {
  // Tweaks ─────────────────────────────
  const [tweaks, setTweaks] = useTweaks({
    theme: "midnight",       // paper | ivory | midnight
    typography: "manuscript", // garamond | caslon | manuscript
    density: "cozy",      // cozy | spacious
    flourishes: true,
    secondPieceCollapsible: true,
    stepByStep: true,        // reveal sections one at a time
  });

  // Which section is the writer up to? Sections beyond `unlockedSection` are
  // hidden when stepByStep is on. Once a section is unlocked, it stays
  // unlocked even if the writer goes back and breaks an earlier field —
  // re-locking would be more annoying than helpful.
  const [unlockedSection, setUnlockedSection] = useState(1);

  // Initial mode picker: every visit opens with a welcome modal that asks
  // what brings the visitor in. "asking" hides everything else until they
  // choose. "format" pre-selects the paid formatting service in the help
  // form; "help" shows the help form with only change/withdraw options.
  // "paid" is set when PayPal Auto Return drops the visitor back on the
  // site with ?paid=1 — shows a payment-received screen.
  // "proof" is the contributor proof round: either chosen from the modal, or
  // entered directly when a proof email's ?k=<token> link lands here.
  type Mode = "asking" | "submit" | "help" | "format" | "paid" | "proof" | "bio" | "art" | "final";
  const initialMode: Mode = (() => {
    if (typeof window === "undefined") return "asking";
    const q = new URLSearchParams(window.location.search);
    // A proof token wins over everything: they clicked a link addressed to
    // them personally, so don't make them choose a door first.
    if (q.get("k")) return "proof";
    // A bio request link: the bio-only page.
    if (q.get("b")) return "bio";
    // The artwork details link from the confirmation email.
    if (q.get("art")) return "art";
    // The editor's final proof (book, cover, front cover), from MWS Desk.
    if (q.get("final")) return "final";
    if (q.get("paid") === "1") return "paid";
    // "Need to correct something?" link in the confirmation email: straight to the help form
    if (q.get("help") === "1") return "help";
    // A token remembered from earlier in the session: refreshing the proof
    // page must not strand the writer at the welcome modal. Ranked below
    // ?paid=1 so PayPal Auto Return isn't hijacked into proof mode.
    if (storedProofToken()) return "proof";
    return "asking";
  })();
  const [mode, setMode] = useState<Mode>(initialMode);

  // If stepByStep is toggled OFF, all sections become visible.
  // If toggled back ON, snap back to wherever the writer was furthest.

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = tweaks.theme;
    root.dataset.typography = tweaks.typography;
    root.dataset.density = tweaks.density;
    root.dataset.flourishes = tweaks.flourishes ? "on" : "off";
  }, [tweaks]);

  // Form state ─────────────────────────
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [genre, setGenre] = useState<Genre | "">("");

  const [title1, setTitle1] = useState("");
  const [body1, setBody1] = useState("");
  const [title2, setTitle2] = useState("");
  const [body2, setBody2] = useState("");
  const [secondOpen, setSecondOpen] = useState(!SECOND_PIECE_DEFAULT_CLOSED);

  // If the writer switches to prose, clear any piece-2 content (prose
  // submissions are limited to one piece). Only clears non-empty content so
  // genre flips don't lose work unnecessarily.
  useEffect(() => {
    if (genre === "prose" && (title2.trim() || plainText(body2).trim())) {
      setTitle2("");
      setBody2("");
      setSecondOpen(false);
    }
  }, [genre]); // eslint-disable-line react-hooks/exhaustive-deps

  const [bio, setBio] = useState("");

  // Dummy "Add a third poem" button — clicking it doesn't add anything, just
  // surfaces a cheeky teasing message. Tracks whether they've clicked it.
  const [thirdNudge, setThirdNudge] = useState(false);

  // Honeypot: invisible to humans, bots tend to fill every named field.
  // If this has a value at submit time, the worker silently rejects.
  const [website, setWebsite] = useState("");

  // Cloudflare Turnstile — verification token captured from the widget.
  // Renders when both (a) the Turnstile script has loaded and (b) the wrapper
  // div has mounted. Step-by-step mode hides section 5 until earlier sections
  // are valid, so the div may mount well after the script. A callback ref +
  // a "ready" effect cover both orderings.
  const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileLoadAttempt, setTurnstileLoadAttempt] = useState(0);
  const turnstileRef = useRef<HTMLElement | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (window.turnstile) { setTurnstileReady(true); return; }
    const SCRIPT_ID = "cf-turnstile-script";
    const existing = document.getElementById(SCRIPT_ID);
    let retryTimer: number | undefined;
    const onLoad = () => {
      setTurnstileReady(true);
      setTurnstileError(null);
    };
    const onError = () => {
      setTurnstileReady(false);
      setTurnstileError("Verification could not load. Check your connection; we'll retry automatically.");
      retryTimer = window.setTimeout(() => {
        document.getElementById(SCRIPT_ID)?.remove();
        setTurnstileLoadAttempt((attempt) => attempt + 1);
      }, 4_000);
    };
    if (existing) {
      existing.addEventListener("load", onLoad, { once: true });
      existing.addEventListener("error", onError, { once: true });
      return () => {
        existing.removeEventListener("load", onLoad);
        existing.removeEventListener("error", onError);
        if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      };
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = onLoad;
    s.onerror = onError;
    document.head.appendChild(s);
    return () => {
      s.onload = null;
      s.onerror = null;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    };
  }, [TURNSTILE_SITE_KEY, turnstileLoadAttempt]);

  const turnstileTheme: "light" | "dark" = tweaks.theme === "midnight" ? "dark" : "light";
  const turnstileAction = mode === "proof" ? PROOF_TURNSTILE_ACTION : "submission";

  const mountTurnstile = useCallback((el: HTMLElement | null) => {
    if (!el) {
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
      turnstileWidgetId.current = null;
      turnstileRef.current = null;
      setTurnstileToken("");
      setTurnstileError(null);
      return;
    }
    turnstileRef.current = el;
    if (!TURNSTILE_SITE_KEY || !window.turnstile || turnstileWidgetId.current) return;
    el.inert = true;
    turnstileWidgetId.current = window.turnstile.render(el, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: turnstileTheme,
      action: turnstileAction,
      appearance: "interaction-only",
      size: "flexible",
      callback: (token) => {
        el.inert = true;
        setTurnstileToken(token);
        setTurnstileError(null);
      },
      "expired-callback": () => setTurnstileToken(""),
      "before-interactive-callback": () => {
        el.inert = false;
        setTurnstileError(null);
      },
      "after-interactive-callback": () => { el.inert = true; },
      "error-callback": () => {
        el.inert = true;
        setTurnstileToken("");
        setTurnstileError("Verification could not load. Check your connection and try again.");
      },
      "unsupported-callback": () => {
        el.inert = true;
        setTurnstileToken("");
        setTurnstileError("This browser cannot run the verification check. Try a current browser.");
      },
    });
  }, [TURNSTILE_SITE_KEY, turnstileTheme, turnstileAction]);

  const resetTurnstile = useCallback(() => {
    if (turnstileRef.current) turnstileRef.current.inert = true;
    if (turnstileWidgetId.current && window.turnstile) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
    setTurnstileToken("");
    setTurnstileError(null);
  }, []);

  useEffect(() => {
    if (turnstileReady && turnstileRef.current && !turnstileWidgetId.current) {
      mountTurnstile(turnstileRef.current);
    }
  }, [turnstileReady, mountTurnstile]);

  // No theme-change effect needed: `mountTurnstile` depends on the theme, so a
  // theme switch gives the callback ref a new identity and React remounts the
  // widget by itself (detach with null → remove, attach → render).

  const [confirms, setConfirms] = useState({
    c1: false, c2: false, c3: false,
    p1: false, p2: false,
    pr1: false, pr2: false, pr3: false,
  });
  const [artworkInterest, setArtworkInterest] = useState(false);

  // ── Local draft (autosave) ─────────────────────────────────────────────
  // `draftOffer` is non-null while the "Welcome back — restore your draft?"
  // modal is showing. Set on mount if a saved draft is found. While it's
  // showing, the welcome modal is suppressed and the save effect pauses
  // (so the offered draft isn't overwritten before they decide).
  const [draftOffer, setDraftOffer] = useState<DraftV1 | null>(null);

  // On mount, check localStorage for a saved draft.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip if dev shortcut `?thanks=...` is in use — landing straight on the
    // success page shouldn't be interrupted by a draft prompt.
    if (new URLSearchParams(window.location.search).has("thanks")) return;
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as DraftV1;
      if (!parsed || parsed.v !== 1) return;
      // Only offer the draft if it has at least some real content. Empty
      // drafts can creep in if a future code path writes one prematurely.
      const hasContent =
        (parsed.first || "").trim() ||
        (parsed.last || "").trim() ||
        (parsed.email || "").trim() ||
        (parsed.title1 || "").trim() ||
        plainText(parsed.body1 || "").trim() ||
        (parsed.title2 || "").trim() ||
        plainText(parsed.body2 || "").trim() ||
        plainText(parsed.bio || "").trim();
      if (!hasContent) {
        window.localStorage.removeItem(DRAFT_KEY);
        return;
      }
      setDraftOffer(parsed);
    } catch {
      // Corrupt JSON — discard silently.
      try { window.localStorage.removeItem(DRAFT_KEY); } catch {}
    }
  }, []);

  // Debounced save: whenever any persisted field changes, serialize the
  // current form state to localStorage after DRAFT_DEBOUNCE_MS of no
  // further changes. Pauses while a draft offer is pending.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (draftOffer) return; // user hasn't decided yet — don't clobber
    // Only save if there's meaningful content to save.
    const hasContent =
      first.trim() ||
      last.trim() ||
      email.trim() ||
      title1.trim() ||
      plainText(body1).trim() ||
      title2.trim() ||
      plainText(body2).trim() ||
      plainText(bio).trim();
    if (!hasContent) return;

    const handle = window.setTimeout(() => {
      try {
        const draft: DraftV1 = {
          v: 1,
          savedAt: Date.now(),
          mode,
          unlockedSection,
          first, last, displayName, email,
          genre: genre || "",
          title1, body1, title2, body2, secondOpen,
          bio,
          confirms,
          artworkInterest,
        };
        window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch {
        // Quota exceeded or storage disabled — fail silently. The form
        // still works; the writer just doesn't get autosave this session.
      }
    }, DRAFT_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [
    draftOffer, mode, unlockedSection,
    first, last, displayName, email, genre,
    title1, body1, title2, body2, secondOpen,
    bio, confirms, artworkInterest,
  ]);

  const restoreDraft = useCallback((d: DraftV1) => {
    setFirst(d.first || "");
    setLast(d.last || "");
    setDisplayName(d.displayName || "");
    setEmail(d.email || "");
    setGenre((d.genre as Genre | "") || "");
    setTitle1(d.title1 || "");
    setBody1(d.body1 || "");
    setTitle2(d.title2 || "");
    setBody2(d.body2 || "");
    setSecondOpen(!!d.secondOpen);
    setBio(d.bio || "");
    if (d.confirms) setConfirms(d.confirms);
    setArtworkInterest(!!d.artworkInterest);
    setUnlockedSection(d.unlockedSection || 1);
    // Skip the welcome modal — they were mid-submission.
    setMode("submit" as Mode);
    setDraftOffer(null);
  }, []);

  const discardDraft = useCallback(() => {
    try { window.localStorage.removeItem(DRAFT_KEY); } catch {}
    setDraftOffer(null);
  }, []);

  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Dev shortcut: append `?thanks=1` (or `?thanks=YourName`) to the URL
  // to land directly on the Thank You page without actually submitting.
  // Drop this when you're done iterating on that page.
  const [submitted, setSubmitted] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).has("thanks");
  });
  const previewName = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("thanks") || ""
    : "";
  const [submitting, setSubmitting] = useState(false);
  const [submissionUncertain, setSubmissionUncertain] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [emailStatus, setEmailStatus] = useState<EmailStatus>("sent");

  // "Are titles in the right field?" confirm before leaving Section II.
  const [titleConfirmOpen, setTitleConfirmOpen] = useState(false);

  // Where the form posts. Same-origin in production (Cloudflare Pages Function).
  // To test against a different host, set window.METHOD_WRITERS_API_URL.
  const apiUrl = (typeof window !== "undefined" && window.METHOD_WRITERS_API_URL) || "/api/submit";

  const emailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), [email]);
  const bioWords = useMemo(() => {
    // Bio now contains italics-only HTML. Strip tags before counting.
    const t = plainText(bio).trim();
    if (!t) return 0;
    return t.split(/\s+/).length;
  }, [bio]);

  // Poetry line accounting (only meaningful when genre === "poetry").
  // Cap is 70 lines total across both poems; blank stanza-break lines count.
  const POETRY_LINE_CAP = 70;
  const poetryLines1 = useMemo(
    () => (genre === "poetry" ? countPoetryLines(body1) : 0),
    [genre, body1]
  );
  const poetryLines2 = useMemo(
    () => (genre === "poetry" ? countPoetryLines(body2) : 0),
    [genre, body2]
  );
  const poetryLinesTotal = poetryLines1 + poetryLines2;
  const poetryOverCap = genre === "poetry" && poetryLinesTotal > POETRY_LINE_CAP;

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!first.trim()) e.first = "Required.";
    if (!last.trim()) e.last = "Required.";
    if (!displayName.trim()) e.displayName = "Required.";
    if (!email.trim()) e.email = "Required.";
    else if (!emailValid) e.email = "Enter a valid email address.";
    if (!genre) e.genre = "Please choose one.";
    if (!title1.trim()) e.title1 = "Required.";
    if (!plainText(body1).trim()) e.body1 = "Please paste the body of your piece.";
    if (poetryOverCap) {
      const over = poetryLinesTotal - POETRY_LINE_CAP;
      e.poetryLines = `Poetry submissions are capped at ${POETRY_LINE_CAP} lines total across both poems (blank lines between stanzas count). You're at ${poetryLinesTotal} — trim ${over} line${over === 1 ? "" : "s"}.`;
    }
    if (!plainText(bio).trim()) e.bio = "Required.";
    else if (bioWords < 7) e.bio = `At least 7 words (you have ${bioWords}).`;
    else if (bioWords > 250) e.bio = `Keep it under 250 words (you have ${bioWords}).`;
    else if (displayName.trim() && !checkNameInFirstSentence(plainText(bio), displayName).ok) {
      e.bio = "Your biography's first sentence must include your name.";
    }
    return e;
  }, [first, last, displayName, email, emailValid, genre, title1, body1, bio, bioWords, poetryOverCap, poetryLinesTotal]);

  const hasErrors = Object.keys(errors).length > 0;

  // Per-section validity — used to gate the "Continue" buttons.
  const sectionValid = useMemo(() => ({
    1: !errors.first && !errors.last && !errors.displayName && !errors.email,
    2: !errors.genre && !errors.title1 && !errors.body1 && !errors.poetryLines,
    3: !errors.bio,
    4: true, // pre-flight checkboxes are optional
    5: true, // review step — nothing to validate
  }), [errors]);

  // Human-readable list of what's missing per section, used in the
  // "Still needed" panel above each Continue button.
  const sectionMissing = useMemo(() => {
    const out: Record<number, string[]> = { 1: [], 2: [], 3: [], 4: [], 5: [] };
    if (errors.first) out[1].push("First name");
    if (errors.last) out[1].push("Last name");
    if (errors.displayName) out[1].push("Pen name (how it should appear in print)");
    if (errors.email) out[1].push(errors.email === "Enter a valid email address." ? "A valid email address" : "Email address");
    if (errors.genre) out[2].push("Choose poetry or prose");
    if (errors.title1) out[2].push("Title of piece one");
    if (errors.body1) out[2].push("Body of piece one");
    if (errors.poetryLines) out[2].push(errors.poetryLines);
    if (errors.bio) out[3].push(errors.bio); // bio errors already have a specific message
    return out;
  }, [errors]);

  // When stepByStep is off, treat all sections as unlocked.
  const visibleSection = (n: number) => !tweaks.stepByStep || unlockedSection >= n;
  const isCurrentSection = (n: number) => tweaks.stepByStep && unlockedSection === n;

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setSubmitAttempted(true);
    setServerErrors([]);

    if (hasErrors) {
      setTimeout(() => {
        const el = document.querySelector("[data-error]");
        if (el) {
          const rect = el.getBoundingClientRect();
          window.scrollTo({ top: window.scrollY + rect.top - 120, behavior: "smooth" });
        }
      }, 20);
      return;
    }

    setSubmitting(true);
    try {
      if (TURNSTILE_SITE_KEY && !turnstileToken) {
        setServerErrors([]);
        setTurnstileError((current) => current ?? "Verification is still loading. Please wait and try again.");
        setSubmitting(false);
        return;
      }
      const payload = {
        first, last, displayName, email, genre,
        title1,
        body1: sanitizeRichText(body1, { preserveStructure: true }).html,
        title2,
        body2: sanitizeRichText(body2, { preserveStructure: true }).html,
        bio,
        confirms,
        artworkInterest,
        website,
        turnstileToken,
      };
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        const errs = (data && data.errors) || [`Server returned ${res.status}. Please try again in a moment.`];
        if (res.status === 502) {
          setSubmissionUncertain(true);
          setServerErrors(["We couldn't confirm whether your submission was saved. Check for a confirmation email or contact the editors before submitting again."]);
        } else {
          setServerErrors(errs);
          resetTurnstile();
        }
        setSubmitting(false);
        return;
      }
      setEmailStatus(data.emailStatus ?? "sent");
      setSubmitting(false);
      setSubmitted(true);
      // Wipe the local draft — they made it through. A returning visitor
      // should see a fresh form, not a prompt to restore yesterday's work.
      try { window.localStorage.removeItem(DRAFT_KEY); } catch {}
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setSubmissionUncertain(true);
      setServerErrors(["We couldn't confirm whether your submission was saved. Check for a confirmation email or contact the editors before submitting again."]);
      setSubmitting(false);
    }
  };

  const shownError = (k: string) => (submitAttempted ? errors[k] : undefined);

  // ────────────────────────────────────
  if (submitted) return <ThankYou onReset={() => window.location.reload()} name={previewName && previewName !== "1" ? previewName : (displayName || `${first} ${last}`)} emailStatus={emailStatus} />;

  // Help/format mode: render the simpler help form instead of the main one.
  if (mode === "help" || mode === "format") {
    return (
      <HelpFormView
        apiUrl={apiUrl}
        track={mode}
        onBackToSubmit={() => setMode("submit")}
      />
    );
  }

  if (mode === "final") {
    return <FinalView onBack={() => { window.history.replaceState(null, "", window.location.pathname); setMode("asking"); }} />;
  }
  if (mode === "art") {
    return <ArtworkView onBack={() => { window.history.replaceState(null, "", window.location.pathname); setMode("asking"); }} />;
  }

  if (mode === "bio") {
    return <BioView onBack={() => { window.history.replaceState(null, "", window.location.pathname); setMode("asking"); }} />;
  }

  // Contributor proof round — its own screen, no masthead or submission form.
  if (mode === "proof") {
    return (
      <ProofView
        onBack={() => setMode("asking")}
        turnstileSiteKey={TURNSTILE_SITE_KEY}
        turnstileToken={turnstileToken}
        turnstileError={turnstileError}
        turnstileRef={mountTurnstile}
        resetTurnstile={resetTurnstile}
      />
    );
  }

  // PayPal Auto Return landed here with ?paid=1 — show the payment-received
  // screen instead of the welcome modal.
  if (mode === "paid") {
    return <PaymentThanks onDismiss={() => {
      // Clear the ?paid=1 from the URL so a refresh doesn't keep showing
      // the thanks screen.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("paid");
        window.history.replaceState({}, "", url.toString());
      } catch {}
      setMode("asking");
    }} />;
  }

  return (
    <div className="page">
      {/* Draft-found prompt takes precedence over the welcome modal — if the
          writer has unfinished work in localStorage, ask whether to restore
          before showing the welcome chooser. */}
      {draftOffer && (
        <DraftFoundDialog
          draft={draftOffer}
          onRestore={() => restoreDraft(draftOffer)}
          onDiscard={discardDraft}
        />
      )}
      {!draftOffer && mode === "asking" && (
        <WelcomeModal
          onPickSubmit={() => setMode("submit")}
          onPickFormat={() => setMode("format")}
          onPickHelp={() => setMode("help")}
          onPickProof={() => setMode("proof")}
        />
      )}
      <Masthead />

      <form className="form" onSubmit={onSubmit} noValidate>
        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
          <label htmlFor="website-url">Website (leave blank)</label>
          <input
            id="website-url"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>
        <FormIntro />

        {/* ── I. Contributor ─────────────────────────────────────── */}
        <section className="section">
          <SectionHeader roman="I" title="Contributor" hint="Who you are, and how we should credit you." />

          <div className="grid-2">
            <Field id="f-first" label="First name" required error={shownError("first")}>
              <TextInput id="f-first" value={first} onChange={setFirst} autoComplete="given-name" invalid={!!shownError("first")} />
            </Field>
            <Field id="f-last" label="Last name" required error={shownError("last")}>
              <TextInput id="f-last" value={last} onChange={setLast} autoComplete="family-name" invalid={!!shownError("last")} />
            </Field>
          </div>

          <Field
            id="f-display"
            label="Name as it should appear in the anthology"
            hint={
              <>
                Use your pen name if you have one. Write it exactly as you'd like it printed —{" "}
                <em>capitalization, accents, spacing and all</em>.
              </>
            }
            required
            error={shownError("displayName")}
          >
            <TextInput id="f-display" value={displayName} onChange={setDisplayName} invalid={!!shownError("displayName")} />
          </Field>

          <Field id="f-email" label="Email address" required error={shownError("email")}>
            <EmailInput id="f-email" value={email} onChange={setEmail} invalid={!!shownError("email")} />
          </Field>

          {displayName.trim() && emailValid && (
            <div className="welcome-pop" role="status" aria-live="polite" key={displayName.trim()}>
              <span className="welcome-pop-ornament" aria-hidden="true">✦</span>
              <div className="welcome-pop-body">
                <p className="welcome-pop-greeting">Hello, {displayName.trim()}!</p>
                <p className="welcome-pop-line">Do you hear that?</p>
                <p className="welcome-pop-tag">
                  That is the sound of your work being published.
                </p>
              </div>
            </div>
          )}

          {isCurrentSection(1) && (
            <>
              {submitAttempted && <MissingList items={sectionMissing[1]} />}
              <ContinueButton
                enabled={sectionValid[1]}
                label="Continue to your work"
                onClick={() => {
                  setUnlockedSection(2);
                  setSubmitAttempted(true);
                }}
                onAttempt={() => setSubmitAttempted(true)}
              />
            </>
          )}
        </section>

        {visibleSection(2) && <Rule />}

        {/* ── II. The Work ───────────────────────────────────────── */}
        {visibleSection(2) && (
          <section className="section">
            <SectionHeader roman="II" title="The Work" hint="Up to two pieces. Poetry or prose — one genre per submission." />

            <Field id="f-genre" label="Is your submission poetry or prose?" required error={shownError("genre")}>
              <GenreSelect value={genre} onChange={(g) => setGenre(g)} />
            </Field>

            {(genre === "prose" || genre === "poetry") && (
              <div className="genre-quip" role="status" aria-live="polite" key={genre}>
                <span className="genre-quip-ornament" aria-hidden="true">✦</span>
                <p className="genre-quip-line">
                  {genre === "prose"
                    ? "Prose chosen. Somewhere a paragraph just stood up a little straighter."
                    : "Poetry. The line break thanks you for your service."}
                </p>
              </div>
            )}

            <EditorsNote tone="caution" title="Read before pasting">
              <p>
                <strong>Do not</strong> type your title into the body box below. Titles go in the
                "Title" field only — duplicating them will print the title twice.
              </p>
              <p>
                Use <strong>spaces</strong>, not tabs, for any alignment inside a stanza or line.
                For italics, select text and press <span className="kbd">I</span>.
              </p>
            </EditorsNote>

            <Piece
              index={1}
              title={title1} setTitle={setTitle1}
              body={body1} setBody={setBody1}
              genre={genre}
              errorTitle={shownError("title1")}
              errorBody={shownError("body1")}
            />

            {/* ── Second piece — poetry only ── */}
            {genre === "poetry" && (
              tweaks.secondPieceCollapsible && !secondOpen ? (
                <button type="button" className="add-second" onClick={() => setSecondOpen(true)}>
                  <span className="add-second-plus">+</span>
                  <span>
                    <span className="add-second-title">Add a second poem</span>
                    <span className="add-second-sub">Optional — maximum of two poems per submission.</span>
                  </span>
                </button>
              ) : (
                <Piece
                  index={2}
                  title={title2} setTitle={setTitle2}
                  body={body2} setBody={setBody2}
                  genre={genre}
                  optional
                  onRemove={tweaks.secondPieceCollapsible ? () => {
                    setTitle2(""); setBody2(""); setSecondOpen(false);
                  } : undefined}
                />
              )
            )}

            {/* ── Dummy "third poem" button — surfaces a teasing nudge ── */}
            {genre === "poetry" && (
              !thirdNudge ? (
                <button type="button" className="add-third" onClick={() => setThirdNudge(true)}>
                  <span className="add-third-plus">+</span>
                  <span>
                    <span className="add-third-title">Add a third poem</span>
                    <span className="add-third-sub">Tempting, isn't it?</span>
                  </span>
                </button>
              ) : (
                <div className="third-nudge" role="status" aria-live="polite">
                  <p className="third-nudge-line">
                    3 pieces? Now {((displayName.trim() || first.trim()).split(/\s+/)[0]) || "friend"}, let's not be greedy 😂
                  </p>
                  <button type="button" className="third-nudge-dismiss" onClick={() => setThirdNudge(false)}>
                    Fine, fine.
                  </button>
                </div>
              )
            )}

            <EditorsNote tone="info" title="A note on quantity">
              {genre === "prose"
                ? "Prose submissions are limited to one piece per writer."
                : genre === "poetry"
                  ? `Poetry submissions accept up to two poems, ${POETRY_LINE_CAP} lines total (blank lines between stanzas count).`
                  : "Up to two poems, or one prose piece."}
            </EditorsNote>

            {genre === "poetry" && (
              <div
                className="poetry-line-tally"
                role="status"
                aria-live="polite"
                style={{
                  margin: "0.75rem 0 0",
                  padding: "0.6rem 0.85rem",
                  border: "1px solid",
                  borderColor: poetryOverCap ? "#b3261e" : "rgba(0,0,0,0.15)",
                  background: poetryOverCap ? "rgba(179,38,30,0.06)" : "rgba(0,0,0,0.03)",
                  color: poetryOverCap ? "#7a1a14" : "inherit",
                  borderRadius: 4,
                  fontSize: "0.92rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: "1rem",
                  flexWrap: "wrap",
                }}
              >
                <span>
                  <strong>{poetryLinesTotal}</strong> / {POETRY_LINE_CAP} lines used
                  {poetryLines2 > 0 && (
                    <span style={{ opacity: 0.7 }}>
                      {" "}
                      (poem 1: {poetryLines1}, poem 2: {poetryLines2})
                    </span>
                  )}
                </span>
                {poetryOverCap && (
                  <span>
                    Over by <strong>{poetryLinesTotal - POETRY_LINE_CAP}</strong> — trim before continuing.
                  </span>
                )}
              </div>
            )}

            {isCurrentSection(2) && (
              <>
                {submitAttempted && <MissingList items={sectionMissing[2]} />}
                <ContinueButton
                  enabled={sectionValid[2]}
                  label="Continue to your biography"
                  onClick={() => {
                    // Before advancing, prompt the writer to confirm their
                    // titles are in the Title fields — not buried in the body.
                    setTitleConfirmOpen(true);
                    setSubmitAttempted(true);
                  }}
                  onAttempt={() => setSubmitAttempted(true)}
                />
              </>
            )}
          </section>
        )}

        {titleConfirmOpen && (
          <TitleConfirmDialog
            hasSecond={!!(title2.trim() || plainText(body2).trim())}
            onCancel={() => setTitleConfirmOpen(false)}
            onConfirm={() => {
              setTitleConfirmOpen(false);
              setUnlockedSection(3);
            }}
          />
        )}

        {visibleSection(3) && <Rule />}

        {/* ── III. Biography ─────────────────────────────────────── */}
        {visibleSection(3) && (
          <section className="section">
            <SectionHeader roman="III" title="Biography" hint="7–250 words, third person, beginning with your name." />

            <EditorsNote tone="info" title="Please note">
              <p>
                Your biography <strong>must</strong> include your name — write in the <em>third person</em> and
                <strong> begin with your name</strong>.
              </p>
              <p className="muted">
                Occasionally italics don't survive the paste. Double-check that all publications,
                journals, film and book titles are italicized.
              </p>
            </EditorsNote>

            <Field
              id="f-bio"
              label="Your biography"
              required
              error={shownError("bio")}
            >
              <PlainTextarea id="f-bio" value={bio} onChange={setBio} maxWords={250} minHeight={200}
                displayName={displayName || `${first} ${last}`.trim()}
                placeholder="e.g., Jordan Rivers is a poet living in Oakland…" />
            </Field>

            {isCurrentSection(3) && (
              <>
                {submitAttempted && <MissingList items={sectionMissing[3]} />}
                <ContinueButton
                  enabled={sectionValid[3]}
                  label="Continue to the pre-flight checklist"
                  onClick={() => {
                    setUnlockedSection(4);
                    setSubmitAttempted(true);
                  }}
                  onAttempt={() => setSubmitAttempted(true)}
                />
              </>
            )}
          </section>
        )}

        {visibleSection(4) && <Rule />}

        {/* ── IV. Pre-flight ─────────────────────────────────────── */}
        {visibleSection(4) && (
          <section className="section">
            <SectionHeader roman="IV" title="Pre-flight" hint="A short checklist before we accept your submission." />

            <div className="checklist">
              <div className="checklist-kicker">Before you submit, please confirm</div>
              <Checkbox id="c1" label="You entered your title only in the Title field, not in the body box." checked={confirms.c1} onChange={(v) => setConfirms({ ...confirms, c1: v })} />
              <Checkbox id="c2" label="You selected the correct genre (poetry or prose)." checked={confirms.c2} onChange={(v) => setConfirms({ ...confirms, c2: v })} />
              <Checkbox id="c3" label="Your name appears above exactly as you want it printed in the anthology." checked={confirms.c3} onChange={(v) => setConfirms({ ...confirms, c3: v })} />

              {genre === "poetry" && (
                <>
                  <div className="checklist-kicker checklist-kicker-sub">If submitting poetry, also confirm</div>
                  <Checkbox id="p1" label="No more than 70 characters per line." checked={confirms.p1} onChange={(v) => setConfirms({ ...confirms, p1: v })} />
                  <Checkbox id="p2" label="Shift + Return for line breaks; Return for stanza breaks." checked={confirms.p2} onChange={(v) => setConfirms({ ...confirms, p2: v })} />
                </>
              )}
              {genre === "prose" && (
                <>
                  <div className="checklist-kicker checklist-kicker-sub">If submitting prose, also confirm</div>
                  <Checkbox id="pr1" label="No tab-indents. Use plain paragraphs with no indentation." checked={confirms.pr1} onChange={(v) => setConfirms({ ...confirms, pr1: v })} />
                  <Checkbox id="pr2" label="Use spaces if you need alignment — tabs aren't supported." checked={confirms.pr2} onChange={(v) => setConfirms({ ...confirms, pr2: v })} />
                  <Checkbox id="pr3" label="Your prose is 500 words or fewer." checked={confirms.pr3} onChange={(v) => setConfirms({ ...confirms, pr3: v })} />
                </>
              )}

              <div className="checklist-kicker checklist-kicker-sub">Optional</div>
              <Checkbox
                id="artwork"
                label="Would you like to submit artwork for consideration in this issue? Not all artwork is accepted, but we always love seeing what writers bring."
                checked={artworkInterest}
                onChange={setArtworkInterest}
              />
              {artworkInterest && (
                <div className="artwork-confirm" role="status">
                  <span className="artwork-confirm-mark" aria-hidden="true">❦</span>
                  <span>
                    <strong>Wonderful.</strong> Your confirmation email will include a link to upload your artwork.
                  </span>
                </div>
              )}
            </div>

            {isCurrentSection(4) && (
              <ContinueButton
                enabled={sectionValid[4]}
                label="Continue to review your submission"
                onClick={() => {
                  setUnlockedSection(5);
                  setSubmitAttempted(true);
                }}
                onAttempt={() => setSubmitAttempted(true)}
              />
            )}
          </section>
        )}

        {visibleSection(5) && <Rule />}

        {/* ── V. Review ─────────────────────────────────────────── */}
        {visibleSection(5) && (
          <section className="section">
            <SectionHeader
              roman="V"
              title="Review"
              hint="A preview of your submission before you send it."
            />

            <EditorsNote tone="info" title="About this preview">
              <p>
                The design and layout in the printed anthology will likely be
                different from what you see here. But the <strong>basics</strong> —
                line breaks, stanza breaks, tab indents, your title and byline,
                and italics — will appear in print as they do in this preview.
              </p>
              <p>
                Read through carefully. If something looks off, scroll up and edit
                the relevant section before submitting.
              </p>
            </EditorsNote>

            <div className="inline-preview">
              <PreviewPage
                displayName={displayName || `${first} ${last}`.trim() || "Unsigned"}
                genre={genre}
                title1={title1}
                body1={body1}
                title2={title2}
                body2={body2}
                bio={bio}
              />
            </div>
          </section>
        )}

        {visibleSection(5) && <Rule />}

        {/* ── Submit ─────────────────────────────────────────────── */}
        {visibleSection(5) && (
          <div className="submit-row">
            {TURNSTILE_SITE_KEY && (
              <div className="turnstile-wrap" style={{ marginBottom: 12 }}>
                <div ref={mountTurnstile} />
              </div>
            )}
            <div className="submit-meta">
              {submitAttempted && hasErrors ? (
                <span className="submit-meta-error" role="alert">
                  {Object.keys(errors).length} field{Object.keys(errors).length === 1 ? "" : "s"} need your attention.
                </span>
              ) : turnstileError ? (
                <span className="submit-meta-error" role="alert">{turnstileError}</span>
              ) : serverErrors.length > 0 ? (
                <span className="submit-meta-error" role="alert">{serverErrors.join(" ")}</span>
              ) : submitting ? (
                <span className="submit-meta-ok">Sending your submission…</span>
              ) : (
                <span className="submit-meta-ok">All submissions will be included in the current issue.</span>
              )}
            </div>
            <div className="submit-actions">
              <button className="submit-btn" type="submit" disabled={submitting || submissionUncertain}>
                <span>{submitting ? "Submitting…" : "Submit to the anthology"}</span>
                <span className="submit-btn-arrow" aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        )}
      </form>

      <Colophon />

      {/* ── Tweaks panel ─────────────────────────────────────────── */}
      <TweaksPanel title="Tweaks">
        <TweakSection label="Palette">
          <TweakRadio
            value={tweaks.theme}
            onChange={(v) => setTweaks('theme', v)}
            options={[
              { value: "paper", label: "Paper" },
              { value: "ivory", label: "Ivory" },
              { value: "midnight", label: "Midnight" },
            ]}
          />
        </TweakSection>
        <TweakSection label="Typography">
          <TweakRadio
            value={tweaks.typography}
            onChange={(v) => setTweaks('typography', v)}
            options={[
              { value: "garamond", label: "Garamond" },
              { value: "caslon", label: "Caslon" },
              { value: "manuscript", label: "Manuscript (mono)" },
            ]}
          />
        </TweakSection>
        <TweakSection label="Density">
          <TweakRadio
            value={tweaks.density}
            onChange={(v) => setTweaks('density', v)}
            options={[
              { value: "cozy", label: "Cozy" },
              { value: "spacious", label: "Spacious" },
            ]}
          />
        </TweakSection>
        <TweakSection label="Options">
          <TweakToggle
            label="Decorative flourishes"
            value={tweaks.flourishes}
            onChange={(v) => setTweaks('flourishes', v)}
          />
          <TweakToggle
            label="Collapse second piece"
            value={tweaks.secondPieceCollapsible}
            onChange={(v) => setTweaks('secondPieceCollapsible', v)}
          />
          <TweakToggle
            label="Reveal sections step-by-step"
            value={tweaks.stepByStep}
            onChange={(v) => {
              setTweaks('stepByStep', v);
              if (!v) setUnlockedSection(5);
            }}
          />
        </TweakSection>
      </TweaksPanel>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function Masthead() {
  return (
    <header className="masthead">
      <h1 className="masthead-title">
        <span className="masthead-title-over">Method Writers</span>
        <span className="masthead-title-main"><em>Speak</em></span>
      </h1>
      <div className="masthead-sub">A literary anthology of poetry &amp; prose</div>
    </header>
  );
}

function FormIntro() {
  return (
    <div className="form-intro">
      <div className="form-intro-kicker">Submission Form</div>
      <h2 className="form-intro-title">Send us your poetry or prose.</h2>
      <p className="form-intro-body">
        Current Method Writing students are welcome to submit; all submissions will be
        automatically included in the current issue. Please follow the instructions precisely so
        your work isn't omitted.
      </p>
      <p className="form-intro-meta">
        <span>≈ 10–15 minutes</span>
        <span className="dot">·</span>
        <span>Up to two pieces</span>
        <span className="dot">·</span>
        <span>Poetry or prose</span>
      </p>
    </div>
  );
}

function Colophon() {
  return (
    <footer className="colophon">
      <div className="colophon-rule" />
      <div className="colophon-text">
        Set in EB Garamond &amp; JetBrains Mono. Edited with a careful hand.
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal shown after a successful .docx import. Reminds the writer that the
// text box is now what will be printed, so they should spot-check stanza
// breaks, line breaks, and the title.
function ImportSuccessModal({ filename, gotTitle, onClose }: { filename: string; gotTitle: boolean; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="import-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="import-modal-title">
      <div className="import-modal">
        <div className="import-modal-kicker">Imported</div>
        <h2 id="import-modal-title" className="import-modal-title">File imported</h2>
        {filename && <div className="import-modal-filename">{filename}</div>}
        <div className="import-modal-body">
          <p>
            <strong>Please double-check that everything imported correctly.</strong>
          </p>
          <p>
            Whatever you see in the text box is how it will appear in print —
            including stanza breaks, line breaks, italics, and the title. Read
            through the body carefully before continuing.
          </p>
          {!gotTitle && (
            <p className="import-modal-note">
              <strong>Note:</strong> We couldn't detect a title in your document — please type it into the Title field.
            </p>
          )}
        </div>
        <div className="import-modal-actions">
          <button type="button" className="import-modal-confirm" onClick={onClose}>
            Got it — I'll review
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Word document import. Uses mammoth.js to convert .docx → HTML in-browser,
// then splits the result into a title (first heading, or first short
// paragraph) and a body. Returns { title, bodyHtml, filename, error }.
async function parseDocxFile(file: File | null | undefined): Promise<{ title?: string; bodyHtml?: string; filename?: string; error?: string }> {
  if (!file) return { error: "No file." } as const;
  const lower = file.name.toLowerCase();
  if (!lower.endsWith(".docx")) {
    return { error: "Please upload a .docx file. Older .doc files aren't supported." };
  }
  let html: string;
  try {
    const mammoth = await import("mammoth/mammoth.browser");
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.convertToHtml({ arrayBuffer });
    html = result.value || "";
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { error: `Couldn't read that file: ${msg || "unknown error"}` };
  }
  if (!html.trim()) return { error: "The document appears to be empty." };

  // Parse the resulting HTML and pull out a title.
  const doc = new DOMParser().parseFromString(`<div id="r">${html}</div>`, "text/html");
  const root = doc.getElementById("r")!;

  let title = "";
  // 1) Prefer a heading element (Word's Heading 1 / Heading 2 styles).
  const heading = root.querySelector("h1, h2, h3");
  if (heading && (heading.textContent || "").trim()) {
    title = (heading.textContent || "").trim();
    heading.remove();
  } else {
    // 2) Otherwise, take the first paragraph if it's short enough.
    const firstP = root.querySelector("p");
    if (firstP) {
      const text = (firstP.textContent || "").trim();
      if (text && text.length <= 80 && !/[.!?]\s/.test(text)) {
        title = text;
        firstP.remove();
      }
    }
  }

  // Strip any residual headings (Word might have multiple) — convert to <p>.
  root.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((h) => {
    const p = doc.createElement("p");
    p.innerHTML = h.innerHTML;
    h.replaceWith(p);
  });

  // Mammoth output still includes bold (<strong>), links (<a>), lists, images,
  // and tables. Run it through the same sanitizer the paste path uses so the
  // imported body honors the editor's italics-only rule.
  const { html: bodyHtml } = sanitizeRichText(root.innerHTML.trim());
  return { title, bodyHtml, filename: file.name };
}

// ─────────────────────────────────────────────────────────────────────────────
// Small panel listing what's still missing in the current section. Rendered
// just above the Continue button when the writer has attempted to advance
// and the section isn't valid yet.
function MissingList({ items }: { items?: string[] }) {
  if (!items || !items.length) return null;
  return (
    <div className="section-missing" role="status">
      <div className="section-missing-head">Still needed</div>
      <ul className="section-missing-list">
        {items!.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Continue button used in step-by-step mode. Disabled until the current
// section's required fields are valid; clicking surfaces section errors so
// the writer can see what to fix.
function ContinueButton({ enabled, label, onClick, onAttempt }: { enabled: boolean; label: string; onClick: () => void; onAttempt?: () => void }) {
  return (
    <div className="continue-row">
      <button
        type="button"
        className="continue-btn"
        aria-disabled={!enabled}
        onClick={() => {
          onAttempt && onAttempt();
          if (enabled) {
            onClick();
            return;
          }
          setTimeout(() => {
            const el = document.querySelector("[data-error]");
            if (el) {
              const rect = el.getBoundingClientRect();
              window.scrollTo({ top: window.scrollY + rect.top - 120, behavior: "smooth" });
            }
          }, 20);
        }}
      >
        <span>{label}</span>
        <span className="continue-btn-arrow" aria-hidden="true">→</span>
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Check whether the title appears at the beginning of the body. Used to
// catch the common "I pasted the title at the top of my body" mistake.
function titleAppearsAtStartOfBody(title: string, body: string): boolean {
  const t = (title || "").trim();
  if (!t || t.length < 3) return false;
  const bodyPlain = plainText(body || "").trim();
  if (!bodyPlain) return false;
  const norm = (s: string) => s
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
  const normT = norm(t);
  if (!normT) return false;
  // Look at the start of the body, up to title-length + a small buffer for
  // punctuation/whitespace tolerance.
  const normBody = norm(bodyPlain.slice(0, t.length + 50));
  return normBody.startsWith(normT);
}

interface PieceProps {
  index: number;
  title: string;
  setTitle: (v: string) => void;
  body: string;
  setBody: (v: string) => void;
  genre: Genre | string;
  errorTitle?: string;
  errorBody?: string;
  optional?: boolean;
  onRemove?: () => void;
}
function Piece({ index, title, setTitle, body, setBody, genre, errorTitle, errorBody, optional, onRemove }: PieceProps) {
  const titleInBody = useMemo(
    () => titleAppearsAtStartOfBody(title, body),
    [title, body]
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  type ImportStatus =
    | { kind: "err"; message: string }
    | { kind: "ok"; filename: string; gotTitle: boolean };
  const [importStatus, setImportStatus] = useState<ImportStatus | null>(null);
  const [importing, setImporting] = useState(false);

  const onFilePicked = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value = ""; // allow re-picking same file later
    if (!file) return;
    setImporting(true);
    setImportStatus(null);
    const result = await parseDocxFile(file);
    setImporting(false);
    if (result.error) {
      setImportStatus({ kind: "err", message: result.error });
      return;
    }
    if (result.title) setTitle(result.title);
    if (result.bodyHtml) setBody(result.bodyHtml);
    setImportStatus({
      kind: "ok",
      filename: result.filename ?? "",
      gotTitle: !!result.title,
    });
  };

  return (
    <div className="piece">
      <div className="piece-head">
        <div className="piece-head-left">
          <div className="piece-num">Piece {index === 1 ? "One" : "Two"}</div>
          {optional && <div className="piece-optional">optional</div>}
        </div>
        <div className="piece-head-right">
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            style={{ display: "none" }}
            onChange={onFilePicked}
          />
          <button
            type="button"
            className="piece-import"
            disabled={importing}
            onClick={() => fileInputRef.current?.click()}
            title="Upload a .docx and we'll auto-fill the title and body"
          >
            <span className="piece-import-icon" aria-hidden="true">
              <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 1h7l3 3v11H2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                <path d="M5 8h4M5 11h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            </span>
            <span className="piece-import-text">
              {importing ? "Importing…" : "Import from Word"}
            </span>
            <span className="piece-import-arrow" aria-hidden="true">↑</span>
          </button>
          {onRemove && (
            <button type="button" className="piece-remove" onClick={onRemove}>
              Remove
            </button>
          )}
        </div>
      </div>

      {/* Errors render inline; success opens a modal so writers actually read the warning. */}
      <div className="piece-hint">
        <span>In the box below, type or paste your piece — or use the </span>
        <strong>Import from Word</strong>
        <span> button above</span>
        <span className="piece-hint-arrow" aria-hidden="true">↗</span>
      </div>

      {importStatus?.kind === "err" && (
        <div className="piece-import-status piece-import-status--err" role="status">
          <span className="piece-import-status-mark" aria-hidden="true">!</span>
          <span>{importStatus.message}</span>
          <button
            type="button"
            className="piece-import-dismiss"
            onClick={() => setImportStatus(null)}
            aria-label="Dismiss"
          >×</button>
        </div>
      )}
      {importStatus?.kind === "ok" && (
        <ImportSuccessModal
          filename={importStatus.filename}
          gotTitle={importStatus.gotTitle}
          onClose={() => setImportStatus(null)}
        />
      )}

      <Field id={`f-title-${index}`} label={`Title of piece ${index === 1 ? "one" : "two"}`} required={index === 1} error={errorTitle}>
        <TextInput id={`f-title-${index}`} value={title} onChange={setTitle} invalid={!!errorTitle} />
      </Field>

      <Field
        id={`f-body-${index}`}
        label="Body of your piece"
        hint={
          genre === "poetry"
            ? "Use Shift + Return for line breaks, Return for stanza breaks. ≤ 70 characters per line."
            : genre === "prose"
              ? "No indents; use plain paragraphs. ≤ 500 words."
              : "Paste only the body. Use the I button for italics."
        }
        required={index === 1}
        error={errorBody}
      >
        <RichText
          id={`f-body-${index}`}
          value={body}
          onChange={setBody}
          minHeight={260}
          maxWords={genre === "prose" ? 500 : undefined}
          maxCharsPerLine={genre === "poetry" ? 70 : undefined}
          genreMode={(genre as Genre) || undefined}
          placeholder="Paste the body of your piece here…"
        />
      </Field>

      {titleInBody && (
        <div className="bio-rule-warn" role="status">
          <span className="bio-rule-warn-mark" aria-hidden="true">!</span>
          <div className="bio-rule-warn-body">
            <div className="bio-rule-warn-title">
              Your title appears at the start of the body. Remove it from the body — titles only go in the Title field, or the anthology will print it twice.
            </div>
            <div className="bio-rule-warn-quote">
              Title: <q>{title}</q>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Print preview — shows the submission as it would appear in the anthology.
// Constrained to ~66 characters per line (the column max-width is 32em in EB
// Garamond at the preview's font-size). Body HTML is rendered as-is since
// the editor sanitizer only allows <em>/<i>/<br>/<p>.
interface PreviewPageProps { displayName: string; genre: Genre | string; title1: string; body1: string; title2: string; body2: string; bio: string }
function PrintPreview({ onClose, ...pageProps }: { onClose: () => void } & PreviewPageProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="pv-overlay" role="dialog" aria-modal="true" aria-label="Print preview">
      <div className="pv-toolbar">
        <span className="pv-toolbar-label">
          Print preview · {pageProps.genre === "poetry" ? "Poetry" : pageProps.genre === "prose" ? "Prose" : "Submission"}
        </span>
        <button type="button" className="pv-close" onClick={onClose} aria-label="Close preview">Close ✕</button>
      </div>
      <div className="pv-scroll">
        <PreviewPage {...pageProps} />
      </div>
    </div>
  );
}

// Reusable preview "page" — used both inside the modal PrintPreview and
// inline inside the Review section of the form.
function PreviewPage({ displayName, genre, title1, body1, title2, body2, bio }: PreviewPageProps) {
  const hasPiece1 = (title1 && title1.trim()) || (plainText(body1).trim());
  const hasPiece2 = (title2 && title2.trim()) || (plainText(body2).trim());
  return (
    <article className={`pv-page pv-page--${genre || "neutral"}`}>
      {(hasPiece1 || hasPiece2) && (
        <h1 className="pv-author">{displayName}</h1>
      )}
      {hasPiece1 && (
        <PreviewPiece title={title1} body={body1} genre={genre} />
      )}
      {hasPiece2 && (
        <>
          <div className="pv-piece-rule" aria-hidden="true">❦</div>
          <PreviewPiece title={title2} body={body2} genre={genre} />
        </>
      )}
      {bio && plainText(bio).trim() && (
        <>
          <div className="pv-section-rule" aria-hidden="true" />
          <section className="pv-bio">
            <div className="pv-bio-kicker">Contributor</div>
            <div className="pv-bio-text" dangerouslySetInnerHTML={{ __html: bio }} />
          </section>
        </>
      )}
      {!hasPiece1 && !hasPiece2 && !plainText(bio || "").trim() && (
        <p className="pv-empty">Nothing to preview yet. Fill in a title and body, or paste a biography.</p>
      )}
    </article>
  );
}

function PreviewPiece({ title, body, genre }: { title: string; body: string; genre: Genre | string }) {
  // Respect an explicit genre selection; otherwise infer it from the body
  // structure. Poetry tends to have <br>s within paragraphs and/or short
  // paragraphs; prose has longer paragraphs with no <br>s.
  const effectiveGenre = inferGenre(body, genre);
  const bodyClass = effectiveGenre === "prose" ? "pv-body pv-body--prose" : "pv-body pv-body--poetry";
  // For prose, strip Word's "blank line" residue (empty <p>, <p><br></p>,
  // bare <br>s between paragraphs) so the tight block layout renders right.
  const renderedBody = effectiveGenre === "prose" ? cleanProseForPreview(body) : body;
  return (
    <section className="pv-piece">
      {title?.trim() && (
        <>
          <h2 className="pv-title">{title}</h2>
          <div className="pv-piece-header-rule" aria-hidden="true" />
        </>
      )}
      {renderedBody && (
        <div
          className={bodyClass}
          dangerouslySetInnerHTML={{ __html: renderedBody }}
        />
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// "Title in the right field?" confirm dialog.
// Shown when the writer hits "Continue to your biography" — a last-chance
// nudge to keep them from duplicating titles in the body field.
function TitleConfirmDialog({
  hasSecond,
  onConfirm,
  onCancel,
}: {
  hasSecond: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="title-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="title-confirm-h">
      <div className="title-confirm-card">
        <div className="title-confirm-kicker">One quick check</div>
        <h2 id="title-confirm-h" className="title-confirm-h">Wait!</h2>
        <p className="title-confirm-body">
          Are you sure your title is <strong>only</strong> in the field labeled{" "}
          <em>Title of Piece One{hasSecond ? " (or Two)" : ""}</em> — and{" "}
          <strong>not</strong> in the Body of Your Piece?
        </p>
        <p className="title-confirm-sub">
          If the title also lives in the body, it will print twice.
        </p>
        <div className="title-confirm-actions">
          <button type="button" className="title-confirm-cancel" onClick={onCancel}>
            Let me check
          </button>
          <button type="button" className="title-confirm-go" onClick={onConfirm} autoFocus>
            Yes, titles are in the right place
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// "Draft found — restore your work?" dialog.
// Shown on page load if a previous autosave is found in localStorage. The
// writer can restore (rehydrates every form field) or discard (wipes the
// saved draft and starts fresh).
function DraftFoundDialog({
  draft,
  onRestore,
  onDiscard,
}: {
  draft: DraftV1;
  onRestore: () => void;
  onDiscard: () => void;
}) {
  // No Escape-to-dismiss here: dismissing without choosing would either
  // discard their work or block the welcome modal, both bad. They must
  // pick Restore or Start fresh.

  // Build a short content summary so they know which draft this is.
  const author = (draft.displayName || `${draft.first} ${draft.last}`.trim()).trim();
  const pieceHints: string[] = [];
  if ((draft.title1 || "").trim()) pieceHints.push(`“${draft.title1.trim()}”`);
  if ((draft.title2 || "").trim()) pieceHints.push(`“${draft.title2.trim()}”`);
  const summary = [author && `by ${author}`, pieceHints.join(" and ")]
    .filter(Boolean).join(" — ");

  return (
    <div
      className="title-confirm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="draft-found-h"
    >
      <div className="title-confirm-card">
        <div className="title-confirm-kicker">Welcome back</div>
        <h2 id="draft-found-h" className="title-confirm-h">
          We saved your draft.
        </h2>
        <p className="title-confirm-body">
          You have a submission in progress from{" "}
          <strong>{formatRelativeTime(draft.savedAt)}</strong>.{" "}
          Restore it and pick up where you left off, or start fresh.
        </p>
        {summary && (
          <p className="title-confirm-sub" style={{ fontStyle: "italic" }}>
            {summary}
          </p>
        )}
        <div className="title-confirm-actions">
          <button
            type="button"
            className="title-confirm-cancel"
            onClick={onDiscard}
          >
            Start fresh
          </button>
          <button
            type="button"
            className="title-confirm-go"
            onClick={onRestore}
            autoFocus
          >
            Restore my draft
          </button>
        </div>
      </div>
    </div>
  );
}

function cleanProseForPreview(html: string): string {
  if (!html) return html;
  try {
    const doc = new DOMParser().parseFromString(`<div id="x">${html}</div>`, "text/html");
    const root = doc.getElementById("x")!;
    // Drop any paragraph whose plain text is empty (after collapsing &nbsp;
    // and stripping whitespace) — including <p><br></p>, <p>&nbsp;</p>, etc.
    root.querySelectorAll("p").forEach((p) => {
      const t = (p.textContent || "").replace(/ /g, " ").trim();
      if (!t) p.remove();
    });
    // Drop bare <br>s that are direct children (between paragraphs).
    Array.from(root.childNodes).forEach((n) => {
      if (n.nodeType === 1 && (n as Element).tagName === "BR") (n as Element).remove();
    });
    return root.innerHTML;
  } catch (e) {
    return html;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
function ThankYou({ name, onReset, emailStatus }: { name: string; onReset: () => void; emailStatus: EmailStatus }) {
  const emailLine = emailStatus === "sent"
    ? <> A confirmation has been sent to your inbox.</>
    : <> The editors will follow up directly — no confirmation email was sent.</>;
  return (
    <div className="page">
      <Confetti />
      <Masthead />
      <div className="thanks">
        <div className="thanks-orn" aria-hidden="true">❦</div>
        <div className="thanks-kicker">Received</div>
        <h2 className="thanks-title">
          Thank you, <em>{name || "friend"}</em>.
        </h2>
        <p className="thanks-body">
          Your submission has been noted by the editors and will appear in the current issue of
          <em> Method Writers Speak</em>.{emailLine}
        </p>
        <button type="button" className="thanks-reset" onClick={onReset}>
          Submit another →
        </button>
      </div>
      <Colophon />
    </div>
  );
}

// Confetti shower for the success page. Pure CSS animation — a flurry of
// little paper bits that fall, sway, and rotate, then disappear. Respects
// `prefers-reduced-motion`.
function Confetti({ count = 90 }: { count?: number }) {
  const pieces = useMemo(() => {
    const colors = [
      "#b3261e", // oxblood
      "#d4a017", // mustard
      "#5d8a5a", // sage
      "#e6c989", // cream
      "#7e6d4e", // walnut
      "#a64a3f", // brick
      "#3d352a", // ink
      "#c97b63", // terracotta
    ];
    const shapes: Array<"rect" | "strip" | "dot"> = ["rect", "strip", "rect", "strip", "dot"];
    return Array.from({ length: count }, (_, i) => {
      const left = Math.random() * 100;
      const baseSize = 5 + Math.random() * 6;
      const shape = shapes[Math.floor(Math.random() * shapes.length)];
      const ratio = shape === "strip" ? 2.2 + Math.random() * 0.8
                  : shape === "dot"   ? 1
                  : 1.3 + Math.random() * 0.8;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const fallDur = 3.6 + Math.random() * 3;
      const swayDur = 1.4 + Math.random() * 1.8;
      const fallDelay = Math.random() * 2.8;
      const swayDelay = Math.random() * 1.5;
      const startRot = Math.random() * 360;
      return {
        i,
        shape,
        style: {
          left: `${left}%`,
          width: `${baseSize}px`,
          height: `${baseSize * ratio}px`,
          background: color,
          borderRadius: shape === "dot" ? "50%" : "1px",
          animationDuration: `${fallDur}s, ${swayDur}s`,
          animationDelay: `${fallDelay}s, ${swayDelay}s`,
          ["--rot-start" as string]: `${startRot}deg`,
        } as React.CSSProperties,
      };
    });
  }, [count]);
  return (
    <div className="confetti" aria-hidden="true">
      {pieces.map((p) => (
        <span key={p.i} className="confetti-piece" style={p.style} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Heuristic genre inference: when the user previews before picking a genre,
// guess from the body structure. Lots of <br>s or short paragraphs → poetry;
// fewer <br>s and long paragraphs → prose.
function inferGenre(body: string, explicit: string): Genre {
  if (explicit === "poetry" || explicit === "prose") return explicit;
  const html = String(body || "");
  if (!html.trim()) return "prose";
  const brCount = (html.match(/<br\b/gi) || []).length;
  const pCount = (html.match(/<p\b/gi) || []).length || 1;
  // Strip tags to measure paragraph length.
  const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const avgParaChars = text.length / pCount;
  // Strong poetry signals: more <br>s than paragraphs, OR short paragraphs.
  if (brCount >= pCount) return "poetry";
  if (avgParaChars < 90) return "poetry";
  return "prose";
}

// ─────────────────────────────────────────────────────────────────────────────
function plainText(html: string): string {
  const d = document.createElement("div");
  d.innerHTML = html || "";
  return d.innerText || "";
}

// Count lines for poetry submission limits.
//
// Rules:
//   • 70 characters across = 1 line. A line longer than 70 chars wraps and
//     counts as ceil(len/70) lines.
//   • <br> (Shift+Enter, "Line" button) ends a line within a stanza — no
//     blank line introduced.
//   • Block boundary (Return, "Stanza" button — produces a new <p>) ends a
//     stanza, which prints with a blank line between stanzas — that blank
//     line counts as 1 line.
//   • Runs of 2+ blank paragraphs collapse to 1, so writers can't pad.
//
// Examples:
//   <p>A<br>B</p>                → 2  (A, B)
//   <p>A</p><p>B</p>             → 3  (A, blank, B)
//   <p>A<br>B</p><p>C<br>D</p>   → 5  (A, B, blank, C, D)
//   71-char single line          → 2  (wraps)
// "Brk-mark" decoration spans (used to visualize line breaks in the editor)
// are stripped here so they don't pollute the count.
const POETRY_CHARS_PER_LINE = 70;
function countPoetryLines(html: string): number {
  if (!html) return 0;
  const doc = new DOMParser().parseFromString(`<div id="__l">${html}</div>`, "text/html");
  const root = doc.getElementById("__l");
  if (!root) return 0;

  // Drop visual-only decoration spans so they don't affect the count.
  Array.from(root.querySelectorAll("span.brk-mark")).forEach((n) => n.remove());

  const BLOCK = new Set(["P", "DIV", "BLOCKQUOTE", "PRE", "H1", "H2", "H3", "H4", "H5", "H6", "LI"]);
  let out = "";
  const walk = (node: Node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === 3) {
        out += child.textContent || "";
      } else if (child.nodeType === 1) {
        const tag = (child as Element).tagName;
        if (tag === "BR") {
          out += "\n";
        } else if (BLOCK.has(tag)) {
          walk(child);
          // Block boundary = stanza break = inserts a blank line.
          out += "\n\n";
        } else {
          walk(child);
        }
      }
    }
  };
  walk(root);

  out = out
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\n+|\n+$/g, "");
  if (!out) return 0;

  // Sum wrapped-line counts. Empty segments (blank lines) count as 1.
  const segments = out.split("\n");
  let total = 0;
  for (const seg of segments) {
    const len = seg.length;
    total += len === 0 ? 1 : Math.ceil(len / POETRY_CHARS_PER_LINE);
  }
  return total;
}

// ─────────────────────────────────────────────────────────────────────────────
// Payment-received screen — shown when PayPal Auto Return drops the writer
// back on the site with ?paid=1 in the URL. Just a thank-you; the editors
// reconcile the payment manually against the existing format-track task.
function PaymentThanks({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="page">
      <Masthead />
      <div className="thanks">
        <div className="thanks-orn" aria-hidden="true">❦</div>
        <div className="thanks-kicker">Payment received</div>
        <h2 className="thanks-title">Thank you.</h2>
        <p className="thanks-body">
          Your $30 formatting payment is in. The editors will match it to your
          submission and begin laying out your piece for the anthology — you'll
          hear from us by email when it's ready for your review.
        </p>
        <button type="button" className="thanks-reset" onClick={onDismiss}>
          Done →
        </button>
      </div>
      <Colophon />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Welcome modal — appears on every page load. Asks whether the visitor is
// submitting a new piece or needs help with an existing submission.
function WelcomeModal({ onPickSubmit, onPickFormat, onPickHelp, onPickProof }: {
  onPickSubmit: () => void;
  onPickFormat: () => void;
  onPickHelp: () => void;
  onPickProof: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const firstOptionRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    const siblings = dialog?.parentElement
      ? Array.from(dialog.parentElement.children).filter((element) => element !== dialog && !element.hasAttribute("inert"))
      : [];

    document.body.style.overflow = "hidden";
    siblings.forEach((element) => { element.setAttribute("inert", ""); });
    const frame = window.requestAnimationFrame(() => firstOptionRef.current?.focus());

    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = prev;
      siblings.forEach((element) => { element.removeAttribute("inert"); });
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  const trapFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab" || !dialogRef.current) return;
    const controls = Array.from(dialogRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"));
    const first = controls[0];
    const last = controls.at(-1);
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div ref={dialogRef} className="welcome-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="welcome-modal-title" onKeyDown={trapFocus}>
      <div className="welcome-modal">
        <div className="welcome-modal-kicker">Method Writers Speak</div>
        <h2 id="welcome-modal-title" className="welcome-modal-title">Welcome — what brings you here?</h2>
        <p className="welcome-modal-body">
          Pick the door that matches your reason for visiting.
        </p>
        <div className="welcome-modal-options">
          <button ref={firstOptionRef} type="button" className="welcome-modal-option welcome-modal-option--primary" onClick={onPickSubmit}>
            <span className="welcome-modal-option-label">I'm submitting a new piece</span>
            <span className="welcome-modal-option-arrow" aria-hidden="true">→</span>
          </button>
          <button type="button" className="welcome-modal-option welcome-modal-option--paid" onClick={onPickFormat}>
            <span className="welcome-modal-option-paid-row">
              <span className="welcome-modal-option-label">I need you to format my piece</span>
              <span className="welcome-modal-option-price">
                <span className="welcome-modal-option-price-symbol" aria-hidden="true">✦</span>
                $30
              </span>
            </span>
            <span className="welcome-modal-option-sub">Editors lay it out for the anthology — paid service.</span>
          </button>
          <button type="button" className="welcome-modal-option welcome-modal-option--proof" onClick={onPickProof}>
            <span className="welcome-modal-option-label">I'm checking my proof</span>
            <span className="welcome-modal-option-sub">Contributors to the current issue — approve your pages or report an error.</span>
          </button>
          <button type="button" className="welcome-modal-option welcome-modal-option--secondary" onClick={onPickHelp}>
            <span className="welcome-modal-option-label">I need help with an existing submission</span>
            <span className="welcome-modal-option-sub">Change something · Withdraw a submission</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Help / change-request form. A simpler form than the main submission flow.
// Posts to /api/help-submit and creates a ClickUp task with a status that
// matches the request type.
function HelpFormView({ apiUrl, track, onBackToSubmit }: { apiUrl: string; track: "help" | "format"; onBackToSubmit: () => void }) {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  // Format track pre-selects "format" and hides the radio buttons. Help
  // track shows only "change" and "withdraw" radios.
  const [requestType, setRequestType] = useState<HelpRequestType | "">(track === "format" ? "format" : "");
  const [pieceTitle, setPieceTitle] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  // File attachment — only collected on the format track.
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
  const ALLOWED_EXT = [".docx", ".doc", ".pdf", ".txt", ".rtf", ".odt", ".pages"];

  const emailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), [email]);

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!first.trim()) e.first = "Required.";
    if (!last.trim()) e.last = "Required.";
    if (!email.trim()) e.email = "Required.";
    else if (!emailValid) e.email = "Enter a valid email address.";
    if (!displayName.trim()) e.displayName = "Required.";
    if (!requestType) e.requestType = "Please choose one.";
    if (!message.trim()) e.message = "Please tell us what you need.";
    return e;
  }, [first, last, email, emailValid, displayName, requestType, message]);

  const shownError = (k: string) => (submitAttempted ? errors[k] : undefined);
  const hasErrors = Object.keys(errors).length > 0;

  const helpApiUrl = apiUrl.replace(/\/submit$/, "/help-submit");

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setSubmitAttempted(true);
    setServerErrors([]);
    if (hasErrors) return;

    setSubmitting(true);
    try {
      // Use multipart FormData so we can ride a file along with the text
      // fields. The worker accepts either JSON or multipart.
      const form = new FormData();
      form.set("first", first);
      form.set("last", last);
      form.set("email", email);
      form.set("displayName", displayName);
      form.set("requestType", String(requestType));
      form.set("pieceTitle", pieceTitle);
      form.set("message", message);
      form.set("website", website);
      form.set("turnstileToken", "");
      if (file) form.set("file", file, file.name);

      const res = await fetch(helpApiUrl, {
        method: "POST",
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        const errs = (data && data.errors) || [`Server returned ${res.status}. Please try again in a moment.`];
        setServerErrors(errs);
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setServerErrors([`Network error: ${msg || "could not reach the server."}`]);
      setSubmitting(false);
    }
  };

  if (submitted) {
    const isFormat = track === "format";
    return (
      <div className="page">
        <Masthead />
        <div className="thanks">
          <div className="thanks-orn" aria-hidden="true">❦</div>
          <div className="thanks-kicker">{isFormat ? "Almost there" : "Received"}</div>
          <h2 className="thanks-title">{isFormat ? "One last step." : "Got it."}</h2>
          {isFormat ? (
            <>
              <p className="thanks-body">
                Your formatting request has been logged. To finish, complete the
                <strong> $30 payment</strong> via PayPal. Once we see your payment,
                an editor will start laying out your piece.
              </p>
              <a
                className="paypal-pay-btn"
                href="https://www.paypal.com/ncp/payment/L9P8JS746X49Q"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="paypal-pay-btn-amount">Pay $30 via PayPal</span>
                <span className="paypal-pay-btn-arrow" aria-hidden="true">→</span>
              </a>
              <p className="thanks-fineprint">
                Payment opens in a new tab. If you've already paid, you can close this — we'll
                match it up on our end and start working on your piece.
              </p>
            </>
          ) : (
            <p className="thanks-body">
              We've passed your request along to the editors. Someone will be in touch
              via the email you provided.
            </p>
          )}
          <button type="button" className="thanks-reset" onClick={() => window.location.reload()}>
            Done →
          </button>
        </div>
        <Colophon />
      </div>
    );
  }

  return (
    <div className="page">
      <Masthead />

      <form className="form" onSubmit={onSubmit} noValidate>
        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
          <input id="hf-website-url" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
        </div>

        <div className="form-intro">
          <div className="form-intro-kicker">
            {track === "format" ? "Formatting service · $30" : "Help / change request"}
          </div>
          <h2 className="form-intro-title">
            {track === "format" ? "Let us format your piece." : "How can we help?"}
          </h2>
          <p className="form-intro-body">
            {track === "format" ? (
              <>
                For a flat <strong>$30</strong>, our editors will lay out your piece
                for the anthology — italics, line breaks, indentation, spacing —
                so it appears in print exactly as it should. Tell us about your
                piece below, and we'll be in touch with payment details and any
                formatting questions.
              </>
            ) : (
              <>For writers who've already submitted and need to change something or withdraw a submission.</>
            )}
          </p>
          <button type="button" className="help-back-link" onClick={onBackToSubmit}>
            ← Actually, I want to submit a new piece
          </button>
        </div>

        <section className="section">
          <SectionHeader roman="?" title="Your details" hint="So we can look up your submission and reply." />
          <div className="grid-2">
            <Field id="hf-first" label="First name" required error={shownError("first")}>
              <TextInput id="hf-first" value={first} onChange={setFirst} autoComplete="given-name" invalid={!!shownError("first")} />
            </Field>
            <Field id="hf-last" label="Last name" required error={shownError("last")}>
              <TextInput id="hf-last" value={last} onChange={setLast} autoComplete="family-name" invalid={!!shownError("last")} />
            </Field>
          </div>
          <Field id="hf-email" label="Email address" required error={shownError("email")}>
            <EmailInput id="hf-email" value={email} onChange={setEmail} invalid={!!shownError("email")} />
          </Field>
          <Field
            id="hf-display"
            label="Name as it should appear in the anthology"
            hint="Use your pen name if you have one. Write it exactly as you'd like it printed."
            required
            error={shownError("displayName")}
          >
            <TextInput id="hf-display" value={displayName} onChange={setDisplayName} invalid={!!shownError("displayName")} />
          </Field>
          <Field id="hf-title" label="Title of the piece this is about (if you know it)">
            <TextInput id="hf-title" value={pieceTitle} onChange={setPieceTitle} />
          </Field>
        </section>

        <Rule />

        <section className="section">
          <SectionHeader
            roman={track === "format" ? "$" : "!"}
            title={track === "format" ? "About your piece" : "What do you need?"}
          />
          {track === "help" && (
            <Field id="hf-type" label="Request type" required error={shownError("requestType")}>
              <div className="help-radio-grid" role="radiogroup">
                <HelpRequestOption
                  value="change"
                  label="I need to change something"
                  sub="Edit a title, body, biography, or detail in your submission."
                  selected={requestType === "change"}
                  onChange={setRequestType}
                />
                <HelpRequestOption
                  value="withdraw"
                  label="Withdraw my submission"
                  sub="Pull a previously submitted piece from this issue."
                  selected={requestType === "withdraw"}
                  onChange={setRequestType}
                />
              </div>
            </Field>
          )}

          <Field
            id="hf-msg"
            label={track === "format" ? "Tell us about the piece" : "Tell us what you need"}
            required
            error={shownError("message")}
            hint={track === "format"
              ? "Paste the piece here, or describe any formatting nuances we should know (italics, indentation, breaks, etc.)."
              : "The more detail, the faster we can help. Quote the original text if you're requesting a change."}
          >
            <textarea
              id="hf-msg"
              className="textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              style={{ minHeight: 220 }}
              placeholder={
                requestType === "format" ? "Paste your piece, or describe any formatting nuances (italics, indents, line/stanza breaks, etc.)."
                  : requestType === "change" ? "What needs to change, and what should it say instead?"
                    : requestType === "withdraw" ? "Anything you'd like us to know about the withdrawal?"
                      : ""
              }
            />
          </Field>

          {track === "format" && (
            <Field
              id="hf-file"
              label="Upload your piece (recommended)"
              hint={`Word (.docx, .doc), PDF, plain text, RTF, ODT, or Pages — up to 10 MB. We'll attach it to your formatting request in our system.`}
              error={fileError || undefined}
            >
              <input
                ref={fileInputRef}
                id="hf-file"
                type="file"
                accept={ALLOWED_EXT.join(",")}
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = ""; // allow re-picking same file
                  setFileError(null);
                  if (!f) { setFile(null); return; }
                  const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
                  if (!ALLOWED_EXT.includes(ext)) {
                    setFileError(`Unsupported file type "${ext}". Use one of: ${ALLOWED_EXT.join(", ")}.`);
                    return;
                  }
                  if (f.size > MAX_FILE_BYTES) {
                    setFileError(`File is ${(f.size / 1024 / 1024).toFixed(1)} MB — keep it under 10 MB.`);
                    return;
                  }
                  setFile(f);
                }}
              />
              <div className="file-picker-row">
                <button
                  type="button"
                  className="file-picker-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {file ? "Choose a different file…" : "Choose a file…"}
                </button>
                {file && (
                  <div className="file-picker-current">
                    <span className="file-picker-current-name">{file.name}</span>
                    <span className="file-picker-current-size">
                      {(file.size / 1024).toFixed(0)} KB
                    </span>
                    <button
                      type="button"
                      className="file-picker-remove"
                      onClick={() => setFile(null)}
                      aria-label="Remove file"
                    >×</button>
                  </div>
                )}
              </div>
            </Field>
          )}
        </section>

        <Rule />

        <div className="submit-row">
          <div className="submit-meta">
            {submitAttempted && hasErrors ? (
              <span className="submit-meta-error">{Object.keys(errors).length} field{Object.keys(errors).length === 1 ? "" : "s"} need your attention.</span>
            ) : serverErrors.length > 0 ? (
              <span className="submit-meta-error">{serverErrors.join(" ")}</span>
            ) : submitting ? (
              <span className="submit-meta-ok">Sending…</span>
            ) : (
              <span className="submit-meta-ok">An editor will follow up by email.</span>
            )}
          </div>
          <button className="submit-btn" type="submit" disabled={submitting}>
            <span>{submitting ? "Sending…" : "Send request"}</span>
            <span className="submit-btn-arrow" aria-hidden="true">→</span>
          </button>
        </div>
      </form>

      <Colophon />
    </div>
  );
}

function HelpRequestOption({ value, label, sub, selected, onChange }: {
  value: HelpRequestType;
  label: string;
  sub: string;
  selected: boolean;
  onChange: (v: HelpRequestType) => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className="help-radio"
      data-selected={selected || undefined}
      onClick={() => onChange(value)}
    >
      <div className="help-radio-mark" aria-hidden="true">
        <span />
      </div>
      <div className="help-radio-body">
        <div className="help-radio-title">{label}</div>
        <div className="help-radio-sub">{sub}</div>
      </div>
    </button>
  );
}

export default App;
